import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    Output,
    QueryList,
    ViewChildren
} from '@angular/core';

import { BehaviorSubject, ReplaySubject } from 'rxjs';

import {
    BaseContainer,
    IconSize,
    mod,
    MultiFormSelectOptions,
    ThesaurusConceptReference
} from '@stylehaven/components';

@Component({
    standalone: false,
    selector: 'med-thesaurus-tag-list',
    templateUrl: './thesaurus-tag-list.component.html'
})
export class ThesaurusTagListComponent extends BaseContainer implements AfterViewChecked {
    public readonly sizes: typeof IconSize = IconSize;

    public tags$: ReplaySubject<MultiFormSelectOptions<ThesaurusConceptReference>> = new ReplaySubject<
        MultiFormSelectOptions<ThesaurusConceptReference>
    >();

    public suggestions$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    private focusIndex: number = 0;

    @Input() id: string;
    @Input() disabled: boolean;
    @Input() multiple: boolean;
    @Input() placeholderMsg = '';

    @Input() set tags(tags: MultiFormSelectOptions<ThesaurusConceptReference>) {
        this.tags$.next(tags);
    }

    @Input() set suggestions(suggestions: string[]) {
        this.suggestions$.next(suggestions);
    }
    @Input() removableSuggestions: boolean = true;

    @Output() valueRemoved: EventEmitter<ThesaurusConceptReference> = new EventEmitter<ThesaurusConceptReference>();
    @Output() suggestionRemoved: EventEmitter<string> = new EventEmitter<string>();

    @ViewChildren('tagItem') optionItems!: QueryList<ElementRef>;

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (this.isActiveComponent()) {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                this.focusIndex = mod(this.focusIndex - 1, this.optionItems.toArray().length);

                this.cdr.markForCheck();
                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                this.focusIndex = mod(this.focusIndex + 1, this.optionItems.toArray().length);

                this.cdr.markForCheck();
                return;
            }

            if (event.key === 'Tab') {
                this.focusIndex = 0;
                this.deactivate();
            }
        }
    }

    // Injectors
    private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        super();
    }

    ngAfterViewChecked(): void {
        if (this.isActiveComponent()) {
            const items = this.optionItems.toArray();
            if (items.length) {
                items[this.focusIndex].nativeElement.focus();
            } else {
                this.focusIndex = 0;
                this.deactivate();
            }
        }
    }

    public removeValue(reference: ThesaurusConceptReference): void {
        this.valueRemoved.emit(reference);
        this.focusIndex = Math.max(0, this.focusIndex - 1);
    }

    public removeSuggestion(suggestion: string): void {
        this.suggestionRemoved.emit(suggestion);
        this.focusIndex = Math.max(0, this.focusIndex - 1);
    }

    public preventDefault(event: Event): void {
        event.preventDefault(); // Tags should only be deleted by pressing backspace or delete
        event.stopPropagation();
    }

    public onFocus(): void {
        this.activate();
    }
}
